import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { cardInfoSelector, cardNumberSelector } from "store/userSliceHelpers";
import { useCardInfo } from "features/cards/useCardInfo";

export interface UseRefreshCardReturn {
  isRefreshing: boolean;
  handleRefresh: () => Promise<void>;
}

export function useRefreshCard(): UseRefreshCardReturn {
  const queryClient = useQueryClient();
  const cardNumber = useSelector(cardNumberSelector);
  const cardInfo = useSelector(cardInfoSelector);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!cardNumber) return;
    try {
      setIsRefreshing(true);
      await Promise.all([
        // The alias comes from here, and the two queries below are gated on it. Without this, a
        // first fetch that failed or landed before the alias was assigned leaves the page on
        // "No card found" and Refresh cannot fix it — those two are disabled, so there is nothing
        // for them to refetch. Re-resolving the alias is what re-enables them. It is also what
        // refreshes the status pill after the issuer blocks or suspends a card mid-session.
        queryClient.invalidateQueries({ queryKey: ["cardInfo", cardNumber] }),
        queryClient.invalidateQueries({
          queryKey: ["cardBalance", cardNumber],
        }),
        queryClient.invalidateQueries({
          queryKey: ["cardTransactions", cardInfo?.cardAlias],
        }),
      ]);
    } catch (err) {
      console.error("refreshCard error:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  return { isRefreshing, handleRefresh };
}
